import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import { getHref } from '../../utils/navigation';
import trackEvent from '../../utils/tracking';
import LinkNoUnderline from '../../atoms/link-no-underline';
import Button from '../../atoms/button';
import Picture from '../../atoms/picture';
import Typography from '../../atoms/typography';
import Ruler from '../../atoms/ruler';

import Link from '../../utils/link';

const HeroWrapper = styled.section`
  margin: -${(props) => props.theme.modularScale['2xlarge']} -3rem 0;
  width: calc(100% + 6rem);

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    margin: 0 auto;
    width: 100%;
  }
`;

const HeroContent = styled.span`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 2rem 3rem 0;

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    margin: 0 auto;
    max-width: 600px;
    width: 100%;
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    ${(props) => (!props.circle ? `
      background-color: var(--background-color);
      margin-top: ${props.hasImages && !props.isStory ? '-60px' : '0'};
      position: relative;
      z-index: ${({ theme }) => theme.layers.backstage};
    ` : null)}
  }
`;

const circleImageStyles = `
  max-width: 430px;
  width: 100%;
`;

const MobileImage = styled(Picture)`
  ${(props) => (props.circle ? circleImageStyles : '')};
  ${(props) => (props.circle ? `padding: ${props.theme.modularScale['2xlarge']} 2rem 0` : '')};

  margin-left: auto;
  margin-right: auto;

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    display: none;
  }
`;

const DesktopImage = styled(Picture)`
  ${(props) => (props.circle ? circleImageStyles : '')}

  display: none;
  margin-left: auto;
  margin-right: auto;

  ${(props) => props.isStory && css`
    img {
      object-fit: contain;
      display: block;
      max-height: 580px;
      max-width: 100%;
    }
  `};

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    display: block;

    & + ${HeroContent} {
      transform: ${(props) => (props.circle || props.isStory ? 'none' : 'translateY(-60px);')}
    }
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    ${(props) => (props.className === 'rect' ? 'min-height: 6rem' : '')};
  }
`;

const Anchor = styled.a`
  display: block;
`;

const HeroTagLine = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  padding-bottom: ${(props) => props.theme.modularScale['2xlarge']};
  padding-top: ${(props) => props.theme.modularScale.base};

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    padding-top: ${(props) => (props.isStory ? props.theme.modularScale.base : '0')};
  }
`;

const HeroTitle = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  margin-bottom: 0.75em;
  text-align: center;
  line-height: 35px;
`;

const HeroDescription = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  text-align: center;

  a {
    font-family: inherit;
    display: inline;
  }
`;

const CreditsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroDl = styled.dl`
  display: grid;
  grid-column-gap: 8px;
  grid-row-gap: 0.7em;
  grid-template-columns: repeat(2, max-content);
  color: ${(props) => props.theme.color.brand};
`;

const HeroDt = styled(Typography)`
`;

const HeroDd = styled(Typography)`
  a {
    display: inline;
  }
`;

const HeroCTA = styled(Button)`
  border: none;
  border-bottom: .2rem solid ${(props) => props.theme.color.brand};
  color: ${(props) => props.theme.color.brand};
  display: inline-block;
  font-family: ${(props) => props.theme.font.caption};
  font-size: ${(props) => props.theme.modularScale.small};
  padding-bottom: .8rem;
  position: relative;
  text-transform: uppercase;
  line-height: 1rem;
  letter-spacing: .24rem;
`;

const contentCheck = (item) => {
  if (
    item.hero_subhead
    || item.hero_heading
    || item.hero_description
    || item.hero_photography
    || item.hero_styling
    || item.hero_writer
    || item.hero_interviewer
    || item.hero_illustration
    || item.hero_cta
  ) {
    return true;
  }
  return false;
};

const HeroImage = (props) => (
  <>
    <MobileImage
      {...props.item.hero_image_mobile}
      circle={props.item.hero_circle_image}
      className={props.item.hero_circle_image ? 'circle' : 'rect'}
      disableLazyload={props.disableLazyload}
      alt={props.item.hero_image_mobile_alt}
    />
    <DesktopImage
      {...props.item.hero_image_desktop}
      circle={props.item.hero_circle_image}
      className={props.item.hero_circle_image ? 'circle' : 'rect'}
      isStory={props.isStory}
      disableLazyload={props.disableLazyload}
      alt={props.item.hero_image_desktop_alt}
    />
  </>
);

HeroImage.propTypes = {
  item: PropTypes.object.isRequired,
  disableLazyload: PropTypes.bool.isRequired,
  isStory: PropTypes.bool.isRequired
};

const Hero = (props) => {
  const [item] = Object.values(props.data.hero_elements);

  const handleTracking = () => trackEvent({
    eventAction: `${props.isStory ? 'Le Scoop Story' : 'Homepage'}`,
    eventCategory: 'Content Engagement',
    eventLabel: 'Content Slot 1 (Hero)'
  });

  return (
    <>
      {
          item && (
            <HeroWrapper onClick={handleTracking}>
              {item?.hero_image_desktop && item?.hero_image_mobile
                ? (
                  <>
                    {
                      item.hero_url ? (
                        <Link href={getHref({ navigation_url: item?.hero_url })} passHref>
                          <Anchor aria-label={item.hero_heading}>
                            <HeroImage
                              item={item}
                              isStory={props.isStory}
                              disableLazyload={props.disableLazyload}
                            />
                          </Anchor>
                        </Link>
                      ) : (
                        <HeroImage
                          item={item}
                          isStory={props.isStory}
                          disableLazyload={props.disableLazyload}
                        />
                      )
                    }
                  </>
                ) : null}
              {contentCheck(item) && (
                <HeroContent
                  circle={item.hero_circle_image}
                  hasImages={item.hero_image_desktop && item.hero_image_desktop}
                  isStory={props.isStory}
                >
                  {
                    item.hero_subhead
                      ? <HeroTagLine element="h2" like="dec-1" isStory={props.isStory}>{item.hero_subhead}</HeroTagLine>
                      : null
                  }

                  {
                    item.hero_heading
                      ? (
                        <>
                          {item.hero_url ? (
                            <LinkNoUnderline
                              href={getHref({ navigation_url: item?.hero_url })}
                            >
                              <HeroTitle element="h1" like="heading-4">{item.hero_heading}</HeroTitle>
                            </LinkNoUnderline>
                          ) : <HeroTitle element="h1" like="heading-4">{item.hero_heading}</HeroTitle>}
                        </>
                      )
                      : null
                  }

                  {
                    item.hero_description
                      ? <HeroDescription element="p" like="paragraph-1" dangerouslySetInnerHTML={{ __html: item.hero_description }} />
                      : null
                  }

                  {
                    (
                      item.hero_photography
                      || item.hero_styling
                      || item.hero_writer
                      || item.hero_interviewer
                      || item.hero_illustration
                    ) && (
                      <CreditsWrapper>
                        {
                          item.hero_photography
                            ? (
                              <HeroDl>
                                <HeroDt element="dt" like="dec-1">Photography</HeroDt>
                                <HeroDd element="dd" like="dec-1" dangerouslySetInnerHTML={{ __html: item.hero_photography }} />
                              </HeroDl>
                            )
                            : null
                        }

                        {
                          item.hero_styling
                            ? (
                              <HeroDl>
                                <HeroDt element="dt" like="dec-1">Styling</HeroDt>
                                <HeroDd element="dd" like="dec-1" dangerouslySetInnerHTML={{ __html: item.hero_styling }} />
                              </HeroDl>
                            )
                            : null
                        }

                        {
                          item.hero_writer
                            ? (
                              <HeroDl>
                                <HeroDt element="dt" like="dec-1">Written By</HeroDt>
                                <HeroDd element="dd" like="dec-1" dangerouslySetInnerHTML={{ __html: item.hero_writer }} />
                              </HeroDl>
                            )
                            : null
                        }

                        {
                          item.hero_interviewer
                            ? (
                              <HeroDl>
                                <HeroDt element="dt" like="dec-1">Interview By</HeroDt>
                                <HeroDd element="dd" like="dec-1" dangerouslySetInnerHTML={{ __html: item.hero_interviewer }} />
                              </HeroDl>
                            )
                            : null
                        }

                        {
                          item.hero_illustration
                            ? (
                              <HeroDl>
                                <HeroDt element="dt" like="dec-1">Illustration</HeroDt>
                                <HeroDd element="dd" like="dec-1" dangerouslySetInnerHTML={{ __html: item.hero_illustration }} />
                              </HeroDl>
                            )
                            : null
                        }
                      </CreditsWrapper>
                    )
                  }

                  {
                    item.hero_cta && item.hero_url
                      // eslint-disable-next-line max-len
                      ? <HeroCTA isLink isText inverted href={item.hero_url}>{item.hero_cta}</HeroCTA>
                      : null
                  }
                </HeroContent>
              )}
            </HeroWrapper>
          )
        }

      {props.data?.hero_hr && <Ruler />}
    </>
  );
};

Hero.defaultProps = {
  isStory: false,
  disableLazyload: false
};

Hero.propTypes = {
  data: PropTypes.object.isRequired,
  isStory: PropTypes.bool,
  disableLazyload: PropTypes.bool
};

Hero.whyDidYouRender = true;

export default Hero;
